import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  FileText, Video, BookOpen, FileSpreadsheet, 
  ArrowRight, Sparkles, Eye, Clock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

const CONTENT_TYPES = {
  article: { label: 'Article', icon: FileText },
  video: { label: 'Video', icon: Video },
  guide: { label: 'Guide', icon: BookOpen },
  worksheet: { label: 'Worksheet', icon: FileSpreadsheet },
};

function RecommendedResources({ fearCluster, archetype, maxItems = 4 }) {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [basedOn, setBasedOn] = useState(null);

  useEffect(() => {
    fetchRecommended();
  }, [fearCluster, archetype]);

  const fetchRecommended = async () => {
    try {
      // Try to get personalized recommendations first
      const response = await axios.get('/api/content/recommended');
      setContent(response.data.content?.slice(0, maxItems) || []);
      setBasedOn(response.data.basedOn);
    } catch (error) {
      // Fallback to filtered content if recommendation fails
      try {
        const params = new URLSearchParams();
        if (fearCluster) params.append('fearCluster', fearCluster);
        if (archetype) params.append('archetype', archetype);
        params.append('limit', maxItems.toString());
        
        const fallbackResponse = await axios.get(`/api/content?${params.toString()}`);
        setContent(fallbackResponse.data.content || []);
      } catch (err) {
        console.error('Failed to fetch resources:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Recommended Resources
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-8">
            <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-primary border-r-transparent"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (content.length === 0) {
    return null; // Don't show section if no content
  }

  return (
    <Card data-testid="recommended-resources">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Recommended Resources
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => window.location.href = '/library'}
          >
            View All
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
        {basedOn && (
          <p className="text-sm text-muted-foreground mt-1">
            Based on your {basedOn.fearCluster && `${basedOn.fearCluster}`}
            {basedOn.fearCluster && basedOn.archetype && ' and '}
            {basedOn.archetype && `${basedOn.archetype}`} profile
          </p>
        )}
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-4">
          {content.map(item => {
            const TypeIcon = CONTENT_TYPES[item.contentType]?.icon || FileText;
            return (
              <div
                key={item.id}
                className="flex gap-3 p-3 rounded-lg border border-border hover:border-primary/50 hover:bg-muted/50 cursor-pointer transition-all group"
                onClick={() => window.location.href = `/library/${item.id}`}
                data-testid={`resource-card-${item.id}`}
              >
                {item.thumbnailUrl ? (
                  <div className="w-20 h-20 rounded-md overflow-hidden flex-shrink-0 bg-muted">
                    <img 
                      src={item.thumbnailUrl} 
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-20 h-20 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                    <TypeIcon className="w-8 h-8 text-muted-foreground" />
                  </div>
                )}
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <TypeIcon className="w-3 h-3 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground capitalize">
                      {CONTENT_TYPES[item.contentType]?.label}
                    </span>
                  </div>
                  
                  <h4 className="font-medium text-sm line-clamp-2 mb-1 group-hover:text-primary transition-colors">
                    {item.title}
                  </h4>
                  
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      {item.viewCount || 0}
                    </span>
                    {item.readTime && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {item.readTime}
                      </span>
                    )}
                    {item.duration && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {item.duration}
                      </span>
                    )}
                  </div>
                </div>
                
                <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity self-center" />
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export default RecommendedResources;
